import { useState, useEffect, useRef } from 'react';
import CourseDetailOverlay from './CourseDetailOverlay';

interface CourseFeature {
  title: string;
  desc: string;
}

interface Course {
  id: number;
  name: string;
  duration: string;
  price: number;
  level: string;
  description: string;
  image: string;
  images?: string[];
  includes: string[];
  features: CourseFeature[];
  dates: string[];
  tags: string[];
}

interface CourseImmersiveProps {
  courses: Course[];
  onBook: (courseName: string) => void;
}

const LEVEL_COLOR: Record<string, string> = {
  初階: 'bg-emerald-100 text-emerald-700',
  進階: 'bg-amber-100 text-amber-700',
};

// 每個課程的主題色調
const COURSE_THEMES = [
  {
    bg: '#1a1208',
    accent: '#c9a96e',
    light: '#f5ede0',
    gradient: 'from-[#1a1208] via-[#2d1f0e] to-[#1a1208]',
  },
  {
    bg: '#0d1a14',
    accent: '#6db88a',
    light: '#e0f5ea',
    gradient: 'from-[#0d1a14] via-[#142b1e] to-[#0d1a14]',
  },
  {
    bg: '#160d1a',
    accent: '#b86db8',
    light: '#f0e0f5',
    gradient: 'from-[#160d1a] via-[#261430] to-[#160d1a]',
  },
];

function useIntersectionObserver(ref: React.RefObject<HTMLElement | null>, threshold = 0.4) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasBeenVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return { isVisible, hasBeenVisible };
}

// 課程區塊：奇數左圖右文，偶數左文右圖
function CourseBlock({
  course,
  index,
  onBook,
  onDetail,
}: {
  course: Course;
  index: number;
  onBook: (name: string) => void;
  onDetail: () => void;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIntersectionObserver(sectionRef as React.RefObject<HTMLElement>, 0.25);
  const theme = COURSE_THEMES[index % COURSE_THEMES.length];
  const isEven = index % 2 === 0;
  const allImages = (course.images && course.images.length > 0) ? course.images : [course.image];
  const [activeImg, setActiveImg] = useState(0);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: theme.bg }}
      id={`course-${course.id}`}
    >
      {/* 背景紋理 */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(ellipse at ${isEven ? '70%' : '30%'} 50%, ${theme.accent}33 0%, transparent 70%)`,
        }}
      />

      {/* 大型背景數字 */}
      <div
        className="absolute select-none pointer-events-none"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(200px, 30vw, 400px)',
          fontWeight: 900,
          color: 'rgba(255,255,255,0.03)',
          lineHeight: 1,
          top: '50%',
          left: isEven ? 'auto' : '-5%',
          right: isEven ? '-5%' : 'auto',
          transform: 'translateY(-50%)',
          transition: 'opacity 1s ease',
          opacity: isVisible ? 1 : 0,
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-16 py-24">
        <div
          className={`flex flex-col lg:flex-row items-center gap-16 ${
            isEven ? '' : 'lg:flex-row-reverse'
          }`}
        >
          {/* 圖片區 */}
          <div
            className="w-full lg:w-1/2 relative"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible
                ? 'translateX(0) scale(1)'
                : isEven
                ? 'translateX(-80px) scale(0.95)'
                : 'translateX(80px) scale(0.95)',
              transition: 'opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 0.9s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            {/* 裝飾框 */}
            <div
              className="absolute rounded-3xl"
              style={{
                inset: '-12px',
                border: `1px solid ${theme.accent}40`,
                transform: isEven ? 'rotate(-1.5deg)' : 'rotate(1.5deg)',
                transition: 'transform 0.9s ease',
              }}
            />
            <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <img
                key={activeImg}
                src={allImages[activeImg]}
                alt={course.name}
                className="w-full h-full object-cover object-center"
                style={{
                  transform: isVisible ? 'scale(1)' : 'scale(1.08)',
                  transition: 'transform 1.2s cubic-bezier(0.4,0,0.2,1)',
                  animation: 'imageFadeIn 0.4s ease',
                }}
              />
              {/* 圖片漸層 */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.accent}20 0%, transparent 60%)`,
                }}
              />
              {/* 圖片張數 badge */}
              {allImages.length > 1 && (
                <div
                  className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', color: theme.accent, border: `1px solid ${theme.accent}50` }}
                >
                  <i className="ri-image-line text-xs"></i>
                  {activeImg + 1} / {allImages.length}
                </div>
              )}
            </div>

            {/* 縮圖列（多張圖片時顯示） */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-4 justify-center">
                {allImages.map((img, ti) => (
                  <button
                    key={ti}
                    onClick={() => setActiveImg(ti)}
                    className="relative rounded-lg overflow-hidden flex-shrink-0 transition-all duration-300 cursor-pointer"
                    style={{
                      width: '60px',
                      height: '44px',
                      border: `2px solid ${ti === activeImg ? theme.accent : 'rgba(255,255,255,0.15)'}`,
                      opacity: ti === activeImg ? 1 : 0.55,
                      transform: ti === activeImg ? 'scale(1.08)' : 'scale(1)',
                    }}
                  >
                    <img src={img} alt={`圖片 ${ti + 1}`} className="w-full h-full object-cover object-top" />
                    {ti === 0 && (
                      <span
                        className="absolute bottom-0 left-0 right-0 text-center text-[9px] font-semibold"
                        style={{ background: theme.accent, color: '#fff', lineHeight: '14px' }}
                      >
                        主圖
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* 浮動價格卡 */}
            <div
              className="absolute rounded-2xl px-6 py-4 shadow-2xl"
              style={{
                background: theme.accent,
                bottom: allImages.length > 1 ? '-76px' : '-24px',
                left: isEven ? 'auto' : '24px',
                right: isEven ? '24px' : 'auto',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 0.5s, transform 0.7s ease 0.5s',
              }}
            >
              <p className="text-white/70 text-xs mb-0.5 font-medium tracking-wider uppercase">課程費用</p>
              <p
                className="text-white text-2xl font-bold"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                NT$ {course.price.toLocaleString()}
              </p>
            </div>
          </div>

          {/* 文字區 */}
          <div className={`w-full lg:w-1/2 ${allImages.length > 1 ? 'mt-16 lg:mt-0' : 'mt-8 lg:mt-0'}`}>
            {/* 序號 + 標籤 */}
            <div
              className="flex items-center gap-4 mb-6"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
              }}
            >
              <span
                className="text-sm font-bold tracking-[0.3em]"
                style={{ color: theme.accent }}
              >
                {String(index + 1).padStart(2, '0')} / {String(3).padStart(2, '0')}
              </span>
              <div className="h-px flex-1" style={{ background: `${theme.accent}40` }} />
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  LEVEL_COLOR[course.level] || 'bg-gray-100 text-gray-600'
                }`}
              >
                {course.level}
              </span>
            </div>

            {/* 課程名稱 */}
            <h2
              className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
              }}
            >
              {course.name}
            </h2>

            {/* 時長 */}
            <div
              className="flex items-center gap-6 mb-6"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s',
              }}
            >
              <span className="flex items-center gap-2 text-white/60 text-sm">
                <i className="ri-time-line" style={{ color: theme.accent }}></i>
                {course.duration}
              </span>
              <span className="flex items-center gap-2 text-white/60 text-sm">
                <i className="ri-group-line" style={{ color: theme.accent }}></i>
                小班制 6 人
              </span>
            </div>

            {/* 描述 */}
            <p
              className="text-white/70 text-base leading-relaxed mb-8 max-w-md"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.7s ease 0.35s, transform 0.7s ease 0.35s',
              }}
            >
              {course.description}
            </p>

            {/* 特色列表 */}
            <div className="space-y-3 mb-8">
              {course.features.map((feature, fi) => (
                <div
                  key={fi}
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-24px)',
                    transition: `opacity 0.6s ease ${0.4 + fi * 0.08}s, transform 0.6s ease ${0.4 + fi * 0.08}s`,
                  }}
                >
                  <div
                    className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                    style={{ background: `${theme.accent}25`, border: `1px solid ${theme.accent}50` }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: theme.accent }}></i>
                  </div>
                  <div>
                    <span className="text-white text-sm font-semibold">{feature.title}</span>
                    <span className="text-white/50 text-sm ml-2">— {feature.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* 標籤 */}
            <div
              className="flex flex-wrap gap-2 mb-10"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.6s ease 0.75s, transform 0.6s ease 0.75s',
              }}
            >
              {course.tags.map((tag, ti) => (
                <span
                  key={ti}
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    background: `${theme.accent}15`,
                    border: `1px solid ${theme.accent}40`,
                    color: theme.accent,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA 按鈕 */}
            <div
              className="flex items-center gap-4"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.7s ease 0.85s, transform 0.7s ease 0.85s',
              }}
            >
              <button
                onClick={() => onBook(course.name)}
                className="group flex items-center gap-3 px-8 py-4 rounded-full font-medium text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{
                  background: theme.accent,
                  color: '#fff',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.04)';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 8px 32px ${theme.accent}60`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
                }}
              >
                立即預約
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </button>
              <button
                onClick={onDetail}
                className="px-6 py-4 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{
                  border: `1px solid ${theme.accent}50`,
                  color: theme.accent,
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = `${theme.accent}15`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                了解更多
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 底部分隔線 */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(to right, transparent, ${theme.accent}40, transparent)` }}
      />

      <style>{`
        @keyframes imageFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </section>
  );
}

// 課程包含區塊（獨立全螢幕）
function CourseIncludesBlock({
  course,
  index,
  onBook,
}: {
  course: Course;
  index: number;
  onBook: (name: string) => void;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIntersectionObserver(sectionRef as React.RefObject<HTMLElement>, 0.2);
  const theme = COURSE_THEMES[index % COURSE_THEMES.length];

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[60vh] flex items-center overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${theme.bg}ee 0%, #0a0a0a 100%)`,
      }}
    >
      <div className="absolute inset-0 opacity-30">
        <img
          src={course.image}
          alt=""
          className="w-full h-full object-cover object-center"
          style={{ filter: 'blur(40px)', transform: 'scale(1.1)' }}
        />
      </div>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.6)' }} />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-8 lg:px-16 py-20">
        <div
          className="text-center mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <p className="text-xs tracking-[0.4em] uppercase mb-3 font-semibold" style={{ color: theme.accent }}>
            What's Included
          </p>
          <h3
            className="text-4xl font-bold text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {course.name} — 課程包含
          </h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {course.includes.map((item, ii) => (
            <div
              key={ii}
              className="text-center p-6 rounded-2xl"
              style={{
                background: `${theme.accent}10`,
                border: `1px solid ${theme.accent}30`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.9)',
                transition: `opacity 0.6s ease ${0.1 + ii * 0.1}s, transform 0.6s ease ${0.1 + ii * 0.1}s`,
              }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-4"
                style={{ background: `${theme.accent}25` }}
              >
                <i className="ri-gift-2-line text-xl" style={{ color: theme.accent }}></i>
              </div>
              <p className="text-white text-sm font-medium leading-snug">{item}</p>
            </div>
          ))}
        </div>

        <div
          className="text-center mt-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease 0.5s, transform 0.7s ease 0.5s',
          }}
        >
          <button
            onClick={() => onBook(course.name)}
            className="px-10 py-4 rounded-full font-medium text-sm tracking-wide text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ background: theme.accent }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
            }}
          >
            預約 {course.name}
          </button>
        </div>
      </div>
    </section>
  );
}

// 頂部進度條
function ScrollProgress({ courses }: { courses: Course[] }) {
  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('courses-section');
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const total = section.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      setProgress(Math.min(Math.max(scrolled / total, 0), 1));

      // 偵測目前在哪個課程
      courses.forEach((c, i) => {
        const el = document.getElementById(`course-${c.id}`);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.top <= window.innerHeight * 0.5 && r.bottom >= window.innerHeight * 0.5) {
            setActiveIndex(i);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [courses]);

  return (
    <>
      {/* 頂部進度條 */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-white/10">
        <div
          className="h-full transition-all duration-100"
          style={{ width: `${progress * 100}%`, background: '#c9a96e' }}
        />
      </div>

      {/* 右側課程導航 */}
      <div className="fixed right-8 top-1/2 -translate-y-1/2 z-[100] flex flex-col gap-3">
        {courses.map((c, i) => (
          <button
            key={c.id}
            onClick={() => {
              document.getElementById(`course-${c.id}`)?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group flex items-center gap-3 cursor-pointer"
            title={c.name}
          >
            <span
              className="text-xs font-medium whitespace-nowrap transition-all duration-300"
              style={{
                color: activeIndex === i ? '#c9a96e' : 'rgba(255,255,255,0.4)',
                opacity: activeIndex === i ? 1 : 0,
                transform: activeIndex === i ? 'translateX(0)' : 'translateX(8px)',
              }}
            >
              {c.name}
            </span>
            <div
              className="rounded-full transition-all duration-300 flex-shrink-0"
              style={{
                width: activeIndex === i ? '10px' : '6px',
                height: activeIndex === i ? '10px' : '6px',
                background: activeIndex === i ? '#c9a96e' : 'rgba(255,255,255,0.3)',
                boxShadow: activeIndex === i ? '0 0 8px #c9a96e80' : 'none',
              }}
            />
          </button>
        ))}
      </div>
    </>
  );
}

// 章節標題區（全螢幕）
function SectionIntro() {
  const ref = useRef<HTMLDivElement>(null);
  const { isVisible } = useIntersectionObserver(ref as React.RefObject<HTMLElement>, 0.3);

  return (
    <div
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center bg-[#0d0d0d] overflow-hidden"
    >
      {/* 背景光暈 */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute rounded-full"
          style={{
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, #c9a96e18 0%, transparent 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>

      <div className="relative z-10 text-center px-8">
        <p
          className="text-xs tracking-[0.6em] uppercase font-semibold mb-8"
          style={{
            color: '#c9a96e',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          Workshop Courses
        </p>

        <h2
          className="text-7xl lg:text-9xl font-bold text-white mb-8 leading-none"
          style={{
            fontFamily: "'Playfair Display', serif",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
            transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
          }}
        >
          課程介紹
        </h2>

        <div
          className="w-24 h-px mx-auto mb-8"
          style={{
            background: '#c9a96e',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
            transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s',
          }}
        />

        <p
          className="text-white/50 text-lg max-w-lg mx-auto leading-relaxed"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.6s, transform 0.8s ease 0.6s',
          }}
        >
          每一堂課都是一場感官的旅程，向下滾動探索我們的課程
        </p>

        {/* 滾動提示 */}
        <div
          className="mt-16 flex flex-col items-center gap-3"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.8s ease 0.9s',
          }}
        >
          <span className="text-white/30 text-xs tracking-[0.3em] uppercase">Scroll to Explore</span>
          <div className="w-px h-16 relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div
              className="absolute top-0 left-0 w-full"
              style={{
                height: '40%',
                background: '#c9a96e',
                animation: 'scrollLine 1.8s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(350%); }
        }
      `}</style>
    </div>
  );
}

// 最終 CTA 區塊
function FinalCTA({ onBook }: { onBook: (name: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const { isVisible } = useIntersectionObserver(ref as React.RefObject<HTMLElement>, 0.3);

  return (
    <div
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0d0d0d]"
    >
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ec79e51f302d6759a6efe97b45c968be.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{
            transform: isVisible ? 'scale(1)' : 'scale(1.08)',
            transition: 'transform 1.5s cubic-bezier(0.4,0,0.2,1)',
          }}
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.5) 100%)' }} />
      </div>

      <div className="relative z-10 text-center px-8 max-w-3xl mx-auto">
        <p
          className="text-xs tracking-[0.5em] uppercase font-semibold mb-6"
          style={{
            color: '#c9a96e',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          Begin Your Journey
        </p>

        <h3
          className="text-6xl lg:text-8xl font-bold text-white mb-8 leading-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
            transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
          }}
        >
          開始您的<br />
          <span style={{ color: '#c9a96e' }}>香氛旅程</span>
        </h3>

        <p
          className="text-white/60 text-lg mb-12 leading-relaxed"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s',
          }}
        >
          每一堂課都是一次感官的探索，讓香氛成為您生活的一部分
        </p>

        <div
          className="flex items-center justify-center gap-4"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.7s, transform 0.8s ease 0.7s',
          }}
        >
          <button
            onClick={() => onBook('')}
            className="px-12 py-5 rounded-full font-medium text-white text-sm tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ background: '#c9a96e' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 12px 40px rgba(201,169,110,0.5)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            立即預約課程
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CourseImmersive({ courses, onBook }: CourseImmersiveProps) {
  const [detailCourse, setDetailCourse] = useState<Course | null>(null);
  const [detailIndex, setDetailIndex] = useState(0);

  const handleOpenDetail = (course: Course, index: number) => {
    setDetailCourse(course);
    setDetailIndex(index);
  };

  const handleCloseDetail = () => {
    setDetailCourse(null);
  };

  return (
    <>
      {detailCourse && (
        <CourseDetailOverlay
          course={detailCourse}
          themeIndex={detailIndex}
          onClose={handleCloseDetail}
          onBook={onBook}
        />
      )}

      <section id="courses-section">
        <ScrollProgress courses={courses} />
        <SectionIntro />
        {courses.map((course, index) => (
          <div key={course.id}>
            <CourseBlock
              course={course}
              index={index}
              onBook={onBook}
              onDetail={() => handleOpenDetail(course, index)}
            />
            <CourseIncludesBlock course={course} index={index} onBook={onBook} />
          </div>
        ))}
        <FinalCTA onBook={onBook} />
      </section>
    </>
  );
}
